import React, { memo, useCallback, useEffect, useState } from "react";
import { TableRow, TableCell, TextField, Select, MenuItem } from "@mui/material";
import { numberInputDisplayValue } from "../../utils/numberInputDisplay";

/**
 * Non-NSE lots = order_qty / lot_size. A plain controlled `type="number"` fights the browser
 * (shows 0200, breaks backspace) because each keystroke re-derives value from parent state.
 * Keep a local string draft while focused/editing; commit numeric lots to parent on each valid parse.
 */
function LotsEditableCell({ row, rowIndex, onLotChange, styles }) {
  const qtyRaw = row.order_quantity ?? row.order_lot;
  const numQty = qtyRaw === "" || qtyRaw == null ? 0 : Number(qtyRaw) || 0;
  const lotSize = Number(row.lot_size) || 1;
  const computedLots = row.lot_size ? Math.max(0, Math.floor(numQty / lotSize)) : 0;

  const [draft, setDraft] = useState(null);
  const rowKey = row.bulk_order_id ?? row.orderBookRowKey ?? `lot-${rowIndex}`;

  useEffect(() => {
    setDraft(null);
  }, [rowKey]);

  const display =
    draft !== null
      ? draft
      : qtyRaw === "" || qtyRaw == null
        ? ""
        : String(computedLots);

  const handleChange = (e) => {
    const raw = e.target.value;
    setDraft(raw);
    if (raw === "" || raw === ".") {
      onLotChange(rowIndex, "", row.lot_size || 1);
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isNaN(n)) {
      const lotsInt = Math.max(0, Math.floor(n));
      onLotChange(rowIndex, lotsInt, row.lot_size || 1);
    }
  };

  const handleBlur = () => {
    setDraft(null);
  };

  if (!row.lot_size) {
    return <TextField size="small" type="text" value="" disabled className={styles["modify-input"]} />;
  }

  return (
    <TextField
      size="small"
      type="text"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      className={styles["modify-input"]}
      inputProps={{ inputMode: "numeric", pattern: "[0-9]*", autoComplete: "off" }}
    />
  );
}

/**
 * Memoized modify-order row so editing one row does not re-render every row
 * when the bulk modify modal has many orders.
 */
function OrderBookModifyBulkFormRow({
  rowIndex,
  row,
  modifyModalSource,
  styles,
  modifySelectMenuProps,
  onFieldChange,
  onLotChange,
  onNSEQtyChange,
}) {
  const onTransactionType = useCallback(
    (e) => onFieldChange(rowIndex, "transaction_type", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onOrderType = useCallback(
    (e) => onFieldChange(rowIndex, "order_type", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onOrderPrice = useCallback(
    (e) => onFieldChange(rowIndex, "order_price", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onDisclosed = useCallback(
    (e) => onFieldChange(rowIndex, "disclosed_quantity", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onProductType = useCallback(
    (e) => onFieldChange(rowIndex, "product_type", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onValidity = useCallback(
    (e) => onFieldChange(rowIndex, "validity", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onNSEQty = useCallback(
    (e) => onNSEQtyChange(rowIndex, e.target.value),
    [rowIndex, onNSEQtyChange]
  );

  const qtyRaw = row.order_quantity ?? row.order_lot;
  const orderQtyNum = qtyRaw === "" || qtyRaw == null ? 0 : Number(qtyRaw) || 0;
  const discRaw = row.disclosed_quantity;
  const disclosedNum = discRaw === "" || discRaw == null ? 0 : Number(discRaw) || 0;
  const disclosedError = disclosedNum > orderQtyNum;

  return (
    <TableRow className={styles["modify-data-row"]}>
      <TableCell className={styles["modify-td"]}>{row.broker_client_code ?? "—"}</TableCell>
      <TableCell className={styles["modify-td"]}>{row.exchange_trading_symbol ?? "—"}</TableCell>
      <TableCell className={styles["modify-td"]}>{row.exchange ?? "—"}</TableCell>
      <TableCell className={styles["modify-td"]}>
        <Select
          size="small"
          value={row.transaction_type}
          onChange={onTransactionType}
          className={styles["modify-select"]}
          MenuProps={modifySelectMenuProps}
        >
          <MenuItem value="BUY">BUY</MenuItem>
          <MenuItem value="SELL">SELL</MenuItem>
        </Select>
      </TableCell>
      <TableCell className={styles["modify-td"]}>
        <Select
          size="small"
          value={row.order_type}
          onChange={onOrderType}
          className={`${styles["modify-select"]} ${styles["modify-order-type-select"]}`}
          MenuProps={modifySelectMenuProps}
        >
          <MenuItem value="LIMIT">LIMIT</MenuItem>
          <MenuItem value="BID_ASK">Bid-Ask</MenuItem>
        </Select>
      </TableCell>
      <TableCell className={styles["modify-td"]}>
        <TextField
          size="small"
          type="number"
          value={numberInputDisplayValue(row.order_price)}
          onChange={onOrderPrice}
          className={styles["modify-input"]}
          inputProps={{ min: 0, step: 0.01 }}
        />
      </TableCell>
      <TableCell className={`${styles["modify-td"]} ${styles["modify-td-lot"]}`}>
        {row.exchange === "NSE" ? (
          <TextField
            size="small"
            type="number"
            value={0}
            disabled
            className={styles["modify-input"]}
            inputProps={{ min: 0, step: 1 }}
            title="Lots are edited on non-NSE rows; not used for NSE quantity entry"
          />
        ) : (
          <LotsEditableCell row={row} rowIndex={rowIndex} onLotChange={onLotChange} styles={styles} />
        )}
      </TableCell>
      <TableCell className={`${styles["modify-td"]} ${styles["modify-td-qty"]}`}>
        {row.exchange === "NSE" ? (
          <TextField
            size="small"
            type="number"
            value={numberInputDisplayValue(qtyRaw)}
            onChange={onNSEQty}
            className={styles["modify-input"]}
            inputProps={{ min: 0 }}
          />
        ) : (
          <TextField
            size="small"
            type="number"
            value={numberInputDisplayValue(qtyRaw)}
            className={styles["modify-input"]}
            inputProps={{ readOnly: true }}
          />
        )}
      </TableCell>
      <TableCell className={`${styles["modify-td"]} ${styles["modify-td-disclosed"]}`}>
        <TextField
          size="small"
          type="number"
          value={row.exchange === "NFO" ? 0 : numberInputDisplayValue(row.disclosed_quantity)}
          onChange={onDisclosed}
          disabled={row.exchange === "NFO"}
          className={`${styles["modify-input"]} ${disclosedError ? styles["modify-input-error"] : ""}`}
          inputProps={{ min: 0 }}
          error={disclosedError}
        />
      </TableCell>
      <TableCell className={`${styles["modify-td"]} ${styles["modify-td-product"]}`}>
        <Select
          size="small"
          value={row.product_type}
          onChange={onProductType}
          className={styles["modify-select"]}
          MenuProps={modifySelectMenuProps}
        >
          <MenuItem value="DELIVERY">DELIVERY</MenuItem>
          <MenuItem value="INTRADAY">INTRADAY</MenuItem>
          {row.exchange !== "NFO" && row.exchange !== "BFO" ? (
            <MenuItem value="MARGIN">MARGIN</MenuItem>
          ) : null}
        </Select>
      </TableCell>
      {modifyModalSource === "bulk" ? (
        <TableCell className={styles["modify-td"]}>
          <Select
            size="small"
            value={row.validity}
            onChange={onValidity}
            className={styles["modify-select"]}
            MenuProps={modifySelectMenuProps}
          >
            <MenuItem value="IOC">IOC</MenuItem>
            <MenuItem value="DAY">DAY</MenuItem>
          </Select>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

/**
 * Memoized initiate row for bulk place modal.
 */
function OrderBookInitiateBulkFormRow({
  rowIndex,
  row,
  styles,
  modifySelectMenuProps,
  onFieldChange,
  onLotChange,
  onNSEQtyChange,
  alternateRow,
}) {
  const onTransactionType = useCallback(
    (e) => onFieldChange(rowIndex, "transaction_type", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onOrderType = useCallback(
    (e) => onFieldChange(rowIndex, "order_type", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onOrderPrice = useCallback(
    (e) => onFieldChange(rowIndex, "order_price", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onDisclosed = useCallback(
    (e) => onFieldChange(rowIndex, "disclosed_quantity", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onProductType = useCallback(
    (e) => onFieldChange(rowIndex, "product_type", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onValidity = useCallback(
    (e) => onFieldChange(rowIndex, "validity", e.target.value),
    [rowIndex, onFieldChange]
  );
  const onNSEQty = useCallback(
    (e) => onNSEQtyChange(rowIndex, e.target.value),
    [rowIndex, onNSEQtyChange]
  );

  const qtyRaw = row.order_quantity ?? row.order_lot;
  const orderQtyNum = qtyRaw === "" || qtyRaw == null ? 0 : Number(qtyRaw) || 0;
  const discRaw = row.disclosed_quantity;
  const disclosedNum = discRaw === "" || discRaw == null ? 0 : Number(discRaw) || 0;
  const disclosedError = disclosedNum > orderQtyNum;

  return (
    <TableRow className={`${styles["initiate-data-row"]} ${alternateRow ? styles["initiate-data-row-alt"] : ""}`}>
      <TableCell className={styles["initiate-td"]}>{row.broker_client_code ?? "—"}</TableCell>
      <TableCell className={styles["initiate-td"]}>{row.exchange_trading_symbol ?? "—"}</TableCell>
      <TableCell className={styles["initiate-td"]}>{row.exchange ?? "—"}</TableCell>
      <TableCell className={styles["initiate-td"]}>
        <Select
          size="small"
          value={row.transaction_type}
          onChange={onTransactionType}
          className={styles["modify-select"]}
          MenuProps={modifySelectMenuProps}
        >
          <MenuItem value="BUY">BUY</MenuItem>
          <MenuItem value="SELL">SELL</MenuItem>
        </Select>
      </TableCell>
      <TableCell className={styles["initiate-td"]}>
        <Select
          size="small"
          value={row.order_type}
          onChange={onOrderType}
          className={`${styles["modify-select"]} ${styles["modify-order-type-select"]}`}
          MenuProps={modifySelectMenuProps}
        >
          <MenuItem value="LIMIT">LIMIT</MenuItem>
          <MenuItem value="BID_ASK">Bid-Ask</MenuItem>
        </Select>
      </TableCell>
      <TableCell className={styles["initiate-td"]}>
        <TextField
          size="small"
          type="number"
          value={numberInputDisplayValue(row.order_price)}
          onChange={onOrderPrice}
          className={styles["modify-input"]}
          inputProps={{ min: 0, step: 0.01 }}
        />
      </TableCell>
      <TableCell className={styles["initiate-td"]}>
        {row.exchange === "NSE" ? (
          <TextField
            size="small"
            type="number"
            value={0}
            disabled
            className={styles["modify-input"]}
            inputProps={{ min: 0, step: 1 }}
            title="Lots are edited on non-NSE rows; not used for NSE quantity entry"
          />
        ) : (
          <LotsEditableCell row={row} rowIndex={rowIndex} onLotChange={onLotChange} styles={styles} />
        )}
      </TableCell>
      <TableCell className={styles["initiate-td"]}>
        {row.exchange === "NSE" ? (
          <TextField
            size="small"
            type="number"
            value={numberInputDisplayValue(qtyRaw)}
            onChange={onNSEQty}
            className={styles["modify-input"]}
            inputProps={{ min: 0 }}
          />
        ) : (
          <TextField
            size="small"
            type="number"
            value={numberInputDisplayValue(qtyRaw)}
            className={styles["modify-input"]}
            inputProps={{ readOnly: true }}
          />
        )}
      </TableCell>
      <TableCell className={styles["initiate-td"]}>
        <TextField
          size="small"
          type="number"
          value={row.exchange === "NFO" ? 0 : numberInputDisplayValue(row.disclosed_quantity)}
          onChange={onDisclosed}
          disabled={row.exchange === "NFO"}
          className={`${styles["modify-input"]} ${disclosedError ? styles["modify-input-error"] : ""}`}
          inputProps={{ min: 0 }}
          error={disclosedError}
        />
      </TableCell>
      <TableCell className={styles["initiate-td"]}>
        <Select
          size="small"
          value={row.product_type}
          onChange={onProductType}
          className={styles["modify-select"]}
          MenuProps={modifySelectMenuProps}
        >
          <MenuItem value="DELIVERY">DELIVERY</MenuItem>
          <MenuItem value="INTRADAY">INTRADAY</MenuItem>
          {row.exchange !== "NFO" && row.exchange !== "BFO" ? (
            <MenuItem value="MARGIN">MARGIN</MenuItem>
          ) : null}
        </Select>
      </TableCell>
      <TableCell className={styles["initiate-td"]}>
        <Select size="small" value={row.validity} onChange={onValidity} className={styles["modify-select"]} MenuProps={modifySelectMenuProps}>
          <MenuItem value="IOC">IOC</MenuItem>
          <MenuItem value="DAY">DAY</MenuItem>
        </Select>
      </TableCell>
    </TableRow>
  );
}

export const MemoOrderBookModifyBulkFormRow = memo(OrderBookModifyBulkFormRow);
export const MemoOrderBookInitiateBulkFormRow = memo(OrderBookInitiateBulkFormRow);
